import React, {useState, useEffect} from 'react';
import { Flex, Text, Table, Avatar, DropdownMenu, Badge, Button, Select } from "@radix-ui/themes";
import { PersonIcon, DotsHorizontalIcon} from "@radix-ui/react-icons";
import { LocationMultiSelect } from "components";
import { useBrandsAndLocations, useUpdateUserInvite } from "hooks";
import { useToast } from "../../context/ToastContext";

export function OrgInvite({ email, token, role, locations, brands = [], handleOrgInviteResend, handleOrgInviteDelete, viewerId }) {

  // OrgInvite specific handlers that pass email and token to the parent component.
  const handleInviteResendClick = (e) => handleOrgInviteResend(email, token);
  const handleInviteDeleteClick = (e) => handleOrgInviteDelete(token);
  const { 
    initialItems,
    locationsData,
    brandsData
  } = useBrandsAndLocations(viewerId);
  const [selectedRole, setSelectedRole] = useState(role.toLowerCase());
  // Calculate independent locations (exclude locations that belong to selected brands)
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState(brands || []);
  
  useEffect(() => {
    // Initialize state from props
    setSelectedRole(role.toLowerCase());
    setSelectedBrands(brands || []);
    
    if (role.toLowerCase() === "owner" && initialItems) {
      // For owners, show all locations and brands
      const allLocationIds = initialItems
        .filter(item => item.type === 'location')
        .map(item => item.id);
      setSelectedLocations(allLocationIds);
    } else if (locations && brands && locationsData && brandsData) {
      // For managers, calculate independent locations (exclude locations that belong to selected brands)
      const brandLocationIds = new Set();
      
      brands.forEach(brandId => {
        // Find all locations that belong to this brand using locationsData
        const brandLocationList = locationsData.filter(location => location.brand_id === brandId);
        brandLocationList.forEach(location => brandLocationIds.add(location.id));
      });
      
      // Filter out locations that belong to selected brands to get independent locations only
      const independentLocations = locations.filter(locId => !brandLocationIds.has(locId));
      setSelectedLocations(independentLocations);
    } else {
      setSelectedLocations(locations || []);
    }
  }, [role, locations, brands, locationsData, brandsData, initialItems]);
  const { mutate: updateUserInvite } = useUpdateUserInvite();
  const { setToastMessage } = useToast();

  const handleSelectionChange = async (values) => {
    const locations = [];
    const brands = [];
    
    values.forEach(value => {
      if (typeof value === 'string' && value.startsWith('brand_')) {
        // Extract brand ID from 'brand_X' format
        const brandId = parseInt(value.replace('brand_', ''));
        brands.push(brandId);
      } else {
        // It's a location ID
        locations.push(value);
      }
    });
    
    // Update local state only (don't use shared hook state)
    setSelectedLocations(locations);
    setSelectedBrands(brands);

    if ((locations && locations.length > 0) || (brands && brands.length > 0)) {
      try{
        await updateUserInvite({
            email: email,
            locations_list: locations,
            brands_list: brands,
            invitation_type: "Manager",
            invitation_status: "Pending"
        });
        setToastMessage("Access Updated");
      } catch (error) {
        setToastMessage("Invite Update Failed");
      }
    } else {
      setToastMessage("Please select at least 1 location or brand");
    }
  }



  const handleUserRoleClick = async (value) => {
    setSelectedRole(value);
    if (value === "owner") {
      const allLocationIds = initialItems
        .filter(item => item.type === 'location')
        .map(item => item.id);
      const allBrandIds = initialItems
        .filter(item => item.type === 'brand')
        .map(item => item.id);
      
      setSelectedLocations(allLocationIds);
      setSelectedBrands(allBrandIds);
      
      try{
        await updateUserInvite({
          email: email,
          locations_list: allLocationIds,
          brands_list: allBrandIds,
          invitation_type: "Owner",
          invitation_status: "Pending"
        });
        setToastMessage("Role Updated");
      } catch (error) {
        setToastMessage("Invite Update Failed");
      }
    } else if (value === "manager") {
      // Keep current selections for manager
      if (selectedLocations.length === 0 && selectedBrands.length === 0) {
        setToastMessage("Please select at least 1 location or brand");
      }
      return;
    }
  };

  return (
    <Table.Row align="center">
      <Table.Cell p="3">
        <Flex align="center" gap="3" style={{ minHeight: '68px', minWidth: 0 }}>
        <Avatar variant="ghost" size="2" style={{ flexShrink: 0 }} fallback={<PersonIcon />} color="blue" />
        <Flex className="text-container" direction="column" justify="center" align="start" gap="var(--space-1)" style={{ flex: '1 0 0', minWidth: 0 }}>
          <Badge>Invite Sent</Badge>
          <Text 
          style={{
            color: "var(--gray-a11)",
            whiteSpace: "nowrap", 
            overflow: "hidden", 
            textOverflow: "ellipsis", 
            maxWidth: "100%"
            }} 
            weight="regular" 
            size="1">
            {email}
          </Text>
        </Flex>
        </Flex>
      </Table.Cell>
       <Table.Cell  
        p="3"
        minWidth="160px"
        style={{
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--space-3)',
        }}
      >
        <Select.Root 
          size="2" 
          defaultValue={selectedRole} 
          onValueChange={handleUserRoleClick}
        >
          <Select.Trigger variant="surface" color="gray" style={{width: "100%"}}/>
          <Select.Content>
            <Select.Group>
              <Select.Item value="owner">Owner</Select.Item>
              <Select.Item value="manager">Manager</Select.Item>
            </Select.Group>
          </Select.Content>
        </Select.Root>
      </Table.Cell>
      <Table.Cell
        p="3"
        size="2"
        style={{
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          alignSelf: "stretch"
        }}
      >
        {selectedRole === "owner" ? 
        <span style={{ paddingLeft: '12px', color: 'var(--gray-a11)' }}>All selected</span> : 
          <LocationMultiSelect
            key={`${email}-${selectedLocations.join(',')}-${selectedBrands.join(',')}`}
            noShowBorder={true}
            options={initialItems}
            defaultValue={[
              ...selectedLocations, 
              ...selectedBrands.map(id => `brand_${id}`)
            ]}
            onValueChange={handleSelectionChange}
            displayTextType="generic"
          />
        }
      </Table.Cell>
      <Table.Cell>
        <Text size="2" color="gray">—</Text>
      </Table.Cell>
      <Table.Cell>
        <Text size="2" color="gray">—</Text>
      </Table.Cell>
      <Table.Cell justify="end">
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <Button variant='ghost' color='gray'>
              <DotsHorizontalIcon />
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content
            size="2"
            variant="solid"
            style={{
              position: 'absolute',
              top: '0',
              left: '100%',
              display: "flex",
              marginLeft: '-120px',
              justifyContent: 'space-between', 
              alignItems: 'center',
              width: '151px',
            }}          
          >
            <DropdownMenu.Item onClick={handleInviteResendClick} >
              Resend Invite
            </DropdownMenu.Item>
            <DropdownMenu.Item color="red" onClick={handleInviteDeleteClick}>
              Cancel Invite
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </Table.Cell>
    </Table.Row>
  );
}